import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors as cartSelectors } from '@elab/core/store/cart';

import { getActions } from './redux';

/**
 * Component is used to display applied coupons/vouchers list
 *
 * @attr {string} delete-voucher-label - Delete card column header in card info table (always empty)
 * @attr {string} valid-coupon-message-prefix - Text description for prefix before coupon code
 * @attr {string} valid-coupon-message - Text description for valid coupon message
 *
 * HTML Structure Example
 *
 * <e2-vouchers-list
 *      valid-coupon-message-prefix="Voucher"
 *      valid-coupon-message="was successfully applied"
 *      delete-voucher-label="remove"
 * >
 * </e2-vouchers-list>
 */

export class VouchersList extends SimpleLitElement {
    static get properties() {
        return {
            storeName: { type: String, attribute: 'store-name' },
            deleteVoucherLabel: {
                type: String,
                attribute: 'delete-voucher-label',
            },
            validCouponMessagePrefix: {
                type: String,
                attribute: 'valid-coupon-message-prefix',
            },
            validCouponMessage: {
                type: String,
                attribute: 'valid-coupon-message',
            },
            showCouponMessage: {
                type: Boolean,
                attribute: 'show-coupon-message',
            },
            showDescription: {
                type: Boolean,
                attribute: 'show-description',
            },
            gtmLabel: { type: String, attribute: 'gtm-label' },
            vouchers: { type: Array, attribute: false },
            showOnlyActiveVouchers: {
                type: Boolean,
                attribute: 'show-only-active-vouchers',
            },
        };
    }

    constructor() {
        super();
        this.deleteVoucherLabel = '';
        this.validCouponMessagePrefix = '';
        this.validCouponMessage = '';
        this.showCouponMessage = false;
        this.showOnlyActiveVouchers = false;
        this.showDescription = false;
    }

    deleteVoucher(detail) {
        this.dispatchEvent(new CustomEvent('delete-voucher', { detail }));
    }

    getItemClassName(item, status) {
        return classMap({
            [`voucher__${item}`]: !!item,
            [`voucher__${item}--deactivated`]: status !== 'ACTIVE',
        });
    }

    vouchersInfoRow = ({ couponCode, status, errorMessage = '', description }) => html`
        <div class="voucher">
            ${this.validCouponMessagePrefix && !errorMessage
                ? html`
                      <span class="voucher__message-prefix">${this.validCouponMessagePrefix}</span>
                  `
                : ''}
            <span class="${this.getItemClassName('code', status)}">
                ${couponCode}
            </span>
            <span class="voucher__remove">
                <i
                    @click=${() => this.deleteVoucher(couponCode)}
                    class="remove-btn icon icon-trash"
                    data-gtm-label="${this.gtmLabel}"
                >
                    ${this.deleteVoucherLabel}
                </i>
            </span>
            ${this.showCouponMessage
                ? html`
                      <span class="${this.getItemClassName('message', status)}">
                          ${errorMessage || this.validCouponMessage}
                      </span>
                  `
                : ''}
            ${description && this.showDescription
                ? html`
                      <span class="voucher__description">
                          ${description}
                      </span>
                  `
                : ''}
        </div>
    `;

    render() {
        return html`
            <div class="vouchers-list__container">
                <div class="vouchers-list">
                    ${this.showOnlyActiveVouchers
                        ? this.vouchers
                              .filter(voucher => voucher.status === 'ACTIVE')
                              .map(this.vouchersInfoRow)
                        : this.vouchers.map(this.vouchersInfoRow)}
                </div>
            </div>
        `;
    }
}

// TODO: dynamically pass storeName
const mapDispatchToEvents = (dispatch, ctx) => ({
    'delete-voucher': ({ detail }) => dispatch(getActions(ctx.storeName).deleteItem(detail)),
});

const mapStateToProps = state => ({
    vouchers: cartSelectors.getCouponDetails(state),
});

export default () =>
    customElements.define(
        'e2-vouchers-list',
        connect(mapStateToProps, mapDispatchToEvents)(VouchersList),
    );
